import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  EyeIcon, EyeSlashIcon, GlobeHemisphereWestIcon, LinkSimpleIcon,
  PencilSimpleIcon, PlusIcon, TrashIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const LOGO_BUCKET = "portal-logos";
const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
const emptyForm = () => ({ title: "", description: "", url: "", active: true, features: "" });

function errorMessage(error) {
  return formatApiErrorDetail(error?.response?.data?.detail || error) || "Something went wrong. Please try again.";
}

function normalizeUrl(value) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export default function PortalAppsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/portal-apps");
      setItems(data || []);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setLogoFile(null);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || "",
      description: item.description || "",
      url: item.url || "",
      active: item.active !== false,
      features: Array.isArray(item.features) ? item.features.join("\n") : "",
    });
    setLogoFile(null);
    setDialogOpen(true);
  };

  const chooseLogo = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) { setLogoFile(null); return; }
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      toast.error("Use a PNG, JPG, WebP, or SVG logo.");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Logo must be 2 MB or smaller.");
      event.target.value = "";
      return;
    }
    setLogoFile(file);
  };

  const uploadLogo = async (file) => {
    const extension = (file.name.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "") || "png";
    const path = `${crypto.randomUUID()}.${extension}`;
    const { error } = await supabase.storage.from(LOGO_BUCKET).upload(path, file, {
      cacheControl: "3600", contentType: file.type, upsert: false,
    });
    if (error) throw error;
    const { data } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(path);
    return { path, url: data.publicUrl };
  };

  const save = async () => {
    const title = form.title.trim();
    const description = form.description.trim();
    const url = normalizeUrl(form.url);
    if (!title || !description || !url) {
      toast.error("Title, description, and URL are required.");
      return;
    }
    try { new URL(url); } catch {
      toast.error("Enter a valid portal URL.");
      return;
    }

    const features = form.features.split("\n").map((f) => f.trim()).filter(Boolean);

    setSaving(true);
    let uploaded = null;
    try {
      if (logoFile) uploaded = await uploadLogo(logoFile);
      const payload = {
        title, description, url, active: form.active, features,
        ...(uploaded ? { logo_url: uploaded.url, logo_path: uploaded.path } : {}),
      };
      if (editing) {
        await api.patch(`/portal-apps/${editing._id}`, payload);
        if (uploaded && editing.logo_path) await supabase.storage.from(LOGO_BUCKET).remove([editing.logo_path]);
        toast.success("Portal updated");
      } else {
        const nextOrder = items.reduce((max, item) => Math.max(max, Number(item.sort_order) || 0), 0) + 10;
        await api.post("/portal-apps", { ...payload, sort_order: nextOrder, icon: "globe" });
        toast.success("Portal added");
      }
      setDialogOpen(false);
      await load();
    } catch (error) {
      if (uploaded?.path) await supabase.storage.from(LOGO_BUCKET).remove([uploaded.path]);
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibility = async (item) => {
    try {
      await api.patch(`/portal-apps/${item._id}`, { active: !item.active });
      toast.success(item.active ? "Portal hidden" : "Portal published");
      await load();
    } catch (error) { toast.error(errorMessage(error)); }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/portal-apps/${deleting._id}`);
      if (deleting.logo_path) await supabase.storage.from(LOGO_BUCKET).remove([deleting.logo_path]);
      toast.success("Portal deleted");
      setDeleting(null);
      await load();
    } catch (error) { toast.error(errorMessage(error)); }
  };

  if (user?.role !== "admin") {
    return <AppLayout><div className="px-8 py-10 text-sm text-[#475569]">Admins only. <Link to="/" className="text-[#0176D3] hover:underline">Back to dashboard</Link></div></AppLayout>;
  }

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4 flex items-center justify-between gap-4">
        <div>
          <div className="trk-uppercase-label">Admin settings</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Digital Portal</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage the apps shown on the Indorama Digital Portal.</p>
        </div>
        <Button onClick={openCreate} data-testid="add-portal-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
          <PlusIcon size={16} weight="bold" className="mr-1" /> Add portal
        </Button>
      </div>

      <div className="px-8 py-6 trk-fade-in">
        {loading ? (
          <div className="border border-dashed border-border rounded-sm p-12 text-center text-sm text-zinc-500">Loading portals...</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-border rounded-sm p-12 text-center">
            <GlobeHemisphereWestIcon size={32} className="mx-auto mb-3 text-zinc-400" />
            <h2 className="font-display text-xl font-bold">No portals yet</h2>
            <p className="text-sm text-zinc-500 mt-1 mb-4">Add the first app to publish it on the Digital Portal.</p>
            <Button onClick={openCreate} className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">Add portal</Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {items.map((item) => (
              <article key={item._id} className={`trk-card p-5 ${item.active ? "" : "opacity-60"}`} data-testid={`portal-card-${item._id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 shrink-0 rounded-md border border-border bg-zinc-50 flex items-center justify-center overflow-hidden">
                    {item.logo_url ? <img src={item.logo_url} alt="" className="w-full h-full object-contain p-1.5" /> : (
                      <span className="font-display text-lg font-bold text-zinc-400" title="No logo uploaded — showing initial">{(item.title || "?").trim().charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="font-display text-lg font-bold leading-tight break-words">{item.title}</h2>
                      <span className={`shrink-0 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${item.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-zinc-200 bg-zinc-100 text-zinc-500"}`}>{item.active ? "Live" : "Hidden"}</span>
                    </div>
                    <p className="text-sm text-zinc-500 mt-1 line-clamp-3">{item.description}</p>
                    <p className="text-xs text-zinc-400 mt-1">{Array.isArray(item.features) && item.features.length ? `${item.features.length} key feature${item.features.length === 1 ? "" : "s"}` : "No key features listed"}{!item.logo_url ? " · No logo uploaded" : ""}</p>
                  </div>
                </div>
                <a href={item.url} target="_blank" rel="noreferrer" className="mt-4 flex items-center gap-1.5 text-xs text-[#0176D3] hover:underline truncate"><LinkSimpleIcon size={13} /> {item.url}</a>
                <div className="mt-4 pt-4 border-t border-border flex items-center justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => toggleVisibility(item)} className="rounded-sm" title={item.active ? "Hide from Digital Portal" : "Publish on Digital Portal"}>
                    {item.active ? <EyeSlashIcon size={14} className="mr-1" /> : <EyeIcon size={14} className="mr-1" />}{item.active ? "Hide" : "Publish"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEdit(item)} className="rounded-sm"><PencilSimpleIcon size={14} className="mr-1" /> Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => setDeleting(item)} className="rounded-sm text-red-600 hover:text-red-700 hover:border-red-300"><TrashIcon size={14} /></Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit portal" : "Add portal"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="trk-uppercase-label mb-2 block">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={120} className="rounded-sm" data-testid="portal-title-input" placeholder="e.g. Vendor Management Portal" /></div>
            <div><Label className="trk-uppercase-label mb-2 block">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500} rows={3} className="rounded-sm" data-testid="portal-description-input" placeholder="A short description shown on the card" /></div>
            <div><Label className="trk-uppercase-label mb-2 block">Portal URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="rounded-sm" data-testid="portal-url-input" placeholder="https://..." /></div>
            <div>
              <Label className="trk-uppercase-label mb-2 block">Key features</Label>
              <Textarea value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} rows={4} className="rounded-sm" data-testid="portal-features-input" placeholder={"One feature per line, e.g.\nLive bidding on export sales orders\nAutomated quote comparison"} />
              <p className="text-xs text-zinc-500 mt-1">Shown as the bullet list on the Digital Portal card. One per line.</p>
            </div>
            <div>
              <Label className="trk-uppercase-label mb-2 block">Logo</Label>
              <Input type="file" accept={ALLOWED_LOGO_TYPES.join(",")} onChange={chooseLogo} className="rounded-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm" data-testid="portal-logo-input" />
              <p className="text-xs text-zinc-500 mt-1">PNG, JPG, WebP, or SVG. Maximum 2 MB.{editing?.logo_url ? " Leave empty to keep the current logo." : ""}</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[#0176D3]" />Publish on the Digital Portal</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]" data-testid="save-portal-button">{saving ? "Saving..." : editing ? "Save changes" : "Add portal"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle className="font-display">Delete {deleting?.title}?</AlertDialogTitle><AlertDialogDescription>This removes the app from the Digital Portal. This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">Delete portal</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
